<script setup>
// Masifi: ajustes de reparto de leads (auto_assignment_config) en Inbox → Colaboradores.
// Guarda SOLO nuestras keys; el backend (masifi_permit_assignment_config.rb) mergea sobre lo guardado,
// así este save y el nativo (max_assignment_limit) no se pisan.
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();

// Default ON ⇒ la ausencia de la key se interpreta como true.
const assignToOfflineAgents = ref(true);
const excludeAdmins = ref(true);
const isUpdating = ref(false);

const setDefaults = () => {
  const config = props.inbox.auto_assignment_config || {};
  assignToOfflineAgents.value = config.assign_to_offline_agents !== false;
  excludeAdmins.value = config.exclude_admins !== false;
};

const updateMasifiAssignment = async () => {
  isUpdating.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      auto_assignment_config: {
        assign_to_offline_agents: assignToOfflineAgents.value,
        exclude_admins: excludeAdmins.value,
      },
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};

// Mismo criterio que CollaboratorsPage: solo re-inicializar al cambiar de inbox.
watch(() => props.inbox.id, setDefaults, { immediate: true });
</script>

<template>
  <div
    class="mt-4 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
  >
    <h4 class="text-heading-3 text-n-slate-12 mb-0.5">
      {{ $t('INBOX_MGMT.MASIFI_ASSIGNMENT.TITLE') }}
    </h4>
    <p class="text-body-main text-n-slate-11 mb-4">
      {{ $t('INBOX_MGMT.MASIFI_ASSIGNMENT.DESCRIPTION') }}
    </p>

    <div class="flex items-start gap-4 justify-between py-2">
      <div class="flex flex-col">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.MASIFI_ASSIGNMENT.OFFLINE_LABEL') }}
        </span>
        <span class="text-body-main text-n-slate-11">
          {{ $t('INBOX_MGMT.MASIFI_ASSIGNMENT.OFFLINE_SUB_TEXT') }}
        </span>
      </div>
      <ToggleSwitch v-model="assignToOfflineAgents" />
    </div>

    <div class="flex items-start gap-4 justify-between py-2">
      <div class="flex flex-col">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.MASIFI_ASSIGNMENT.EXCLUDE_ADMINS_LABEL') }}
        </span>
        <span class="text-body-main text-n-slate-11">
          {{ $t('INBOX_MGMT.MASIFI_ASSIGNMENT.EXCLUDE_ADMINS_SUB_TEXT') }}
        </span>
      </div>
      <ToggleSwitch v-model="excludeAdmins" />
    </div>

    <div class="flex justify-end mt-4">
      <NextButton
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :is-loading="isUpdating"
        @click="updateMasifiAssignment"
      />
    </div>
  </div>
</template>
